/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { context } from "@opentelemetry/api";
import * as gcpMetadata from "gcp-metadata";

import { ComponentLogger, SemConvResource, Attributes, dtSuppressTracing } from "@dynatrace/opentelemetry-core";

// ============================================================================

export const cFunctionName	= process.env.K_SERVICE ?? "";

// ============================================================================

class GcfResourceDetector {
	private readonly _logger: ComponentLogger = new ComponentLogger("GcfResourceDetector"); // TODO exchange with OTel logger

	// ----------------------------------------------------------------------------
	async detect(): Promise<Attributes> {
		const attrs: Attributes = {
			[SemConvResource.CLOUD_PROVIDER]: SemConvResource.CloudProviderValues.GCP,
			[SemConvResource.CLOUD_PLATFORM]: SemConvResource.CloudPlatformValues.GCP_CLOUD_FUNCTIONS
		};

		let projectId = "";
		let region = "";
		await context.with(dtSuppressTracing(context.active()), async () => {
			if (await gcpMetadata.isAvailable()) {
				[projectId, region] = await Promise.all([
					this._getProjectId(),
					this._getRegion()
				]);
			} else {
				this._logger.warn("GCP Metadata Server not available.");
			}
		});

		this._addAttribute(attrs, SemConvResource.FAAS_NAME, cFunctionName);
		this._addAttribute(attrs, SemConvResource.FAAS_VERSION, process.env.K_REVISION);
		this._addAttribute(attrs, SemConvResource.CLOUD_ACCOUNT_ID, projectId);
		this._addAttribute(attrs, SemConvResource.CLOUD_REGION, region);
		this._addAttribute(attrs, SemConvResource.GCP_REGION, region);
		this._addAttribute(attrs, SemConvResource.GCP_PROJECT_ID, projectId);
		this._addAttribute(attrs, SemConvResource.GCP_INSTANCE_NAME, cFunctionName);

		this._addFaasId(attrs, cFunctionName, projectId, region);

		return attrs;
	}

	// ----------------------------------------------------------------------------
	private _addAttribute(attrs: Attributes, key: string, value: string | undefined) {
		if (value) {
			attrs[key] = value;
		} else {
			this._logger.warn(`Could not detect resource "${key}"`);
		}
	}

	// ----------------------------------------------------------------------------
	private _addFaasId(attrs: Attributes, functionName: string | undefined, projectId: string, region: string) {
		const key = SemConvResource.FAAS_ID;
		if (functionName && projectId && region) {
			// ID in resource name format: https://cloud.google.com/asset-inventory/docs/resource-name-format
			attrs[key] = `//cloudfunctions.googleapis.com/projects/${projectId}/locations/${region}/functions/${functionName}`;
		} else {
			this._logger.warn(`Could not detect resource "${key}`);
		}
	}

	// ----------------------------------------------------------------------------
	private async _getProjectId(): Promise<string> {
		try {
			return await gcpMetadata.project("project-id");
		} catch {
			return "";
		}
	}

	// ----------------------------------------------------------------------------
	private async _getRegion(): Promise<string> {
		try {
			// returned string should have the form  "projects/<numeric-project-id>/regions/<region>""
			const region: string = await gcpMetadata.instance("region");
			if (region) {
				return region.split("/").pop() || "";
			}
		} catch {
			// do nothing
		}
		return "";
	}
}

export const gcfDetector = new GcfResourceDetector();
